import React from 'react';
import { Alert, StyleSheet, Text } from 'react-native';
import { useNavigation } from '@react-navigation/native';

import { CalcScreen, CategoryCard, ListRow } from '../../components/shell';
import { C, FONT, SP } from '../../theme/tokens';

type Calc = { label: string; description: string };
type HydronicCategory = {
  title: string;
  description: string;
  emoji: string;
  calculations: Calc[];
};

const HYDRONIC_CATEGORIES: HydronicCategory[] = [
  {
    title: 'Pump Laws',
    description: 'Pump performance and efficiency calculations',
    emoji: '⚡',
    calculations: [
      { label: 'Water Horsepower', description: 'Theoretical WHP from GPM, head, and specific gravity' },
      { label: 'Brake Horsepower', description: 'Shaft BHP from water HP and pump efficiency' },
      { label: 'Pump Efficiency', description: 'Efficiency as the ratio of water HP to brake HP' },
    ],
  },
  {
    title: 'Delta P, Cv, and GPM',
    description: 'Pressure drop and flow rate calculations',
    emoji: '📊',
    calculations: [
      { label: 'Coil Delta P', description: 'Pressure drop across a coil from GPM and Cv' },
      { label: 'Cv and GPM', description: 'Solve valve Cv, GPM, or ΔP from the Cv equation' },
    ],
  },
  {
    title: 'Heat Transfer',
    description: 'Heat transfer calculations for hydronic systems',
    emoji: '🔥',
    calculations: [
      { label: 'Heat Transfer Calculations', description: 'Water-side Q = 500 × GPM × ΔT for load or flow' },
    ],
  },
  {
    title: 'Boiler',
    description: 'Boiler performance and operating calculations',
    emoji: '🏭',
    calculations: [
      { label: 'Output', description: 'Net boiler output from input rating and efficiency' },
      { label: 'Fire Rate', description: 'Fuel burn rate (GPH) for a given boiler output' },
      { label: 'Operating Cost', description: 'Hourly or annual fuel cost from fire rate and fuel price' },
    ],
  },
];

const SCREEN_MAP: Record<string, string> = {
  'Water Horsepower': 'HydronicPumpLawsWaterHorsepower',
  'Brake Horsepower': 'HydronicPumpLawsBrakeHorsepower',
  'Pump Efficiency': 'HydronicPumpEfficiency',
  'Coil Delta P': 'HydronicDeltaPCoilDeltaP',
  'Cv and GPM': 'HydronicDeltaPCvandGPM',
  'Heat Transfer Calculations': 'HydronicHeatTransferCalculations',
  'Output': 'HydronicBoilerOutput',
  'Fire Rate': 'HydronicBoilerFireRate',
  'Operating Cost': 'HydronicBoilerOperatingCost',
};

const TABHydronicScreen = () => {
  const navigation = useNavigation();

  const handleCalculationPress = (calculation: string) => {
    const screenName = SCREEN_MAP[calculation];
    if (screenName) {
      navigation.navigate(screenName as never);
    } else {
      Alert.alert('Coming Soon', `${calculation} calculator will be available soon!`);
    }
  };

  return (
    <CalcScreen title="Hydronic Calculations" subtitle="TAB · Hydronic">
      <Text style={styles.sectionLabel}>Hydronic Categories</Text>
      {HYDRONIC_CATEGORIES.map((category) => (
        <CategoryCard
          key={category.title}
          emoji={category.emoji}
          title={category.title}
          description={category.description}
        >
          {category.calculations.map((calculation, i) => (
            <ListRow
              key={calculation.label}
              index={i}
              label={calculation.label}
              description={calculation.description}
              onPress={() => handleCalculationPress(calculation.label)}
            />
          ))}
        </CategoryCard>
      ))}
    </CalcScreen>
  );
};

const styles = StyleSheet.create({
  sectionLabel: {
    fontFamily: FONT.sans,
    fontSize: 12,
    color: C.dim,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    marginBottom: SP.sm,
  },
});

export default TABHydronicScreen;
